import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Chip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { statsApi } from '../../services/api';
import ProductPairDrawer from './ProductPairDrawer';
import DisconnectModal from './DisconnectModal';
import OverridesTab from './OverridesTab';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

// Color scale for heat map (lighter to darker)
const getHeatColor = (value, maxValue) => {
  if (!value || !maxValue) return 'transparent';
  const intensity = Math.min(value / maxValue, 1);
  const alpha = 0.1 + intensity * 0.5;
  return `rgba(25, 118, 210, ${alpha})`;
};

// Match type color mapping (per spec)
const MATCH_TYPE_CONFIG = {
  manual_demo: { color: '#9C27B0', label: 'Manual', bgColor: '#F3E5F5' },
  gtin_direct: { color: '#4CAF50', label: 'GTIN Direct', bgColor: '#E8F5E9' },
  gtin_transitive: { color: '#FF9800', label: 'GTIN Transitive', bgColor: '#FFF3E0' },
  hub_transitive: { color: '#009688', label: 'Hub Transitive', bgColor: '#E0F2F1' },
  embedding: { color: '#2196F3', label: 'Embedding', bgColor: '#E3F2FD' },
  gtin_legacy: { color: '#9E9E9E', label: 'GTIN (Legacy)', bgColor: '#F5F5F5' },
};

// Helper to render match type chip with optional extra info
const MatchTypeChip = ({ matchType, gtinCount, hops, sharedHubs, similarity }) => {
  const config = MATCH_TYPE_CONFIG[matchType] || MATCH_TYPE_CONFIG.embedding;
  let label = config.label;

  // Add extra info for special types
  if (matchType === 'gtin_direct' && gtinCount) {
    label = `${config.label} (${gtinCount} GTINs)`;
  } else if (matchType === 'gtin_transitive' && hops) {
    label = `${config.label} (${hops} hops)`;
  } else if (matchType === 'hub_transitive' && sharedHubs) {
    label = `${config.label} (${sharedHubs} hubs)`;
  }

  return (
    <Tooltip title={`${matchType}: ${((similarity || 0) * 100).toFixed(0)}% similarity`}>
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 500,
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Tooltip>
  );
};

function StatCard({ title, value, subtitle, color = 'primary' }) {
  // Map color to MUI theme color or use text.secondary for default/grey
  const textColor = color === 'default' ? 'text.secondary' : `${color}.main`;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div" color={textColor}>
          {value?.toLocaleString() ?? '-'}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function RetailerPairsTab() {
  const [matrixData, setMatrixData] = useState(null);
  const [selectedPair, setSelectedPair] = useState(null);
  const [pairDetails, setPairDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [relationshipType, setRelationshipType] = useState('IDENTICAL');
  // Server-side search and pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Debounced input
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsPagination, setProductsPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [matchTypeFilter, setMatchTypeFilter] = useState('');
  const matchTypeFilterRef = useRef(matchTypeFilter);
  matchTypeFilterRef.current = matchTypeFilter;
  // Product pair drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProductPair, setSelectedProductPair] = useState({ productAId: null, productBId: null });
  // Disconnect modal state
  const [disconnectModal, setDisconnectModal] = useState({ open: false, uuidA: null, uuidB: null });
  // Trend chart state
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendDays, setTrendDays] = useState(30);

  const handleProductRowClick = (match) => {
    setSelectedProductPair({
      productAId: match.product_a_id,
      productBId: match.product_b_id,
    });
    setDrawerOpen(true);
  };

  const loadMatrixData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await statsApi.getAllRetailerPairs({ relationship_type: relationshipType });
      setMatrixData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [relationshipType]);

  const loadPairDetails = useCallback(async (retailerA, retailerB) => {
    try {
      setDetailLoading(true);
      setError(null);
      const data = await statsApi.getRetailerPairStats(retailerA, retailerB, {
        include_products: false, // We load products separately with pagination
        relationship_type: relationshipType,
      });
      setPairDetails(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, [relationshipType]);

  const loadProducts = useCallback(async (retailerA, retailerB, search = '', offset = 0) => {
    try {
      setProductsLoading(true);
      const data = await statsApi.getRetailerPairProducts(retailerA, retailerB, {
        search: search || undefined,
        limit: 50,
        offset,
        relationship_type: relationshipType,
        match_type: matchTypeFilterRef.current || undefined,
      });
      setProducts(data.matched_products || []);
      setProductsPagination({
        total: data.total_count || 0,
        limit: data.limit || 50,
        offset: data.offset || 0,
        hasMore: data.has_more || false,
      });
    } catch (err) {
      console.error('Error loading products:', err);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, [relationshipType]);

  const loadTrendData = useCallback(async (retailerA, retailerB) => {
    try {
      setTrendLoading(true);
      const data = await statsApi.getRetailerPairHistory(retailerA, retailerB, {
        days: trendDays,
        relationship_type: relationshipType,
      });
      // Format dates for chart display
      const formatted = (data.snapshots || []).map((s) => ({
        ...s,
        dateLabel: new Date(s.snapshot_date + 'T00:00:00').toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
      }));
      setTrendData(formatted);
    } catch (err) {
      console.error('Error loading trend data:', err);
      setTrendData([]);
    } finally {
      setTrendLoading(false);
    }
  }, [trendDays, relationshipType]);

  useEffect(() => {
    loadMatrixData();
  }, [loadMatrixData]);

  // Handle relationship type change
  const handleRelationshipTypeChange = (event, newType) => {
    if (newType !== null) {
      setRelationshipType(newType);
      setSelectedPair(null); // Reset selection when changing type
      setPairDetails(null);
    }
  };

  useEffect(() => {
    if (selectedPair) {
      loadPairDetails(selectedPair.retailerA, selectedPair.retailerB);
      // Reset search/filter and load products
      setSearchQuery('');
      setSearchInput('');
      setMatchTypeFilter('');
      loadProducts(selectedPair.retailerA, selectedPair.retailerB, '', 0);
      loadTrendData(selectedPair.retailerA, selectedPair.retailerB);
    }
  }, [selectedPair, loadPairDetails, loadProducts, loadTrendData]);

  // Debounced search effect
  useEffect(() => {
    if (!selectedPair) return;
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        loadProducts(selectedPair.retailerA, selectedPair.retailerB, searchInput, 0);
      }
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [searchInput, selectedPair, searchQuery, loadProducts]);

  // Reload products when match type filter changes
  useEffect(() => {
    if (!selectedPair) return;
    loadProducts(selectedPair.retailerA, selectedPair.retailerB, searchQuery, 0);
  }, [matchTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCellClick = (retailerA, retailerB) => {
    // For VARIANT_SAME: only allow clicking diagonal (same retailer)
    // For other types: only allow clicking off-diagonal (different retailers)
    if (relationshipType === 'VARIANT_SAME') {
      if (retailerA !== retailerB) return;
    } else {
      if (retailerA === retailerB) return;
    }
    setSelectedPair({ retailerA, retailerB });
  };

  const handleBackToMatrix = () => {
    setSelectedPair(null);
    setPairDetails(null);
    setTrendData([]);
  };

  if (loading && !matrixData) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !matrixData) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Build matrix from pairs data
  const retailers = matrixData?.retailers || [];
  const pairsMap = {};
  let maxValue = 0;
  (matrixData?.pairs || []).forEach((pair) => {
    const key = `${pair.retailer_a}|${pair.retailer_b}`;
    const reverseKey = `${pair.retailer_b}|${pair.retailer_a}`;
    pairsMap[key] = pair;
    pairsMap[reverseKey] = pair;
    if (pair.total > maxValue) maxValue = pair.total;
  });

  // Detail view
  if (selectedPair && pairDetails) {
    const retailerAName = retailers.find((r) => r.id === pairDetails.retailer_a)?.name || pairDetails.retailer_a;
    const retailerBName = retailers.find((r) => r.id === pairDetails.retailer_b)?.name || pairDetails.retailer_b;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBackToMatrix} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">
            {retailerAName} ↔ {retailerBName}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => loadPairDetails(selectedPair.retailerA, selectedPair.retailerB)}
              disabled={detailLoading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {detailLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, md: 2.4 }}>
                <StatCard
                  title="Total Matches"
                  value={pairDetails.total || pairDetails.total_unique || 0}
                  subtitle="All relationships"
                  color="primary"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2.4 }}>
                <StatCard
                  title="Embedding"
                  value={pairDetails.by_match_type?.embedding || 0}
                  subtitle="AI similarity only"
                  color="info"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2.4 }}>
                <StatCard
                  title="GTIN Direct"
                  value={pairDetails.by_match_type?.gtin_direct || 0}
                  subtitle="Barcode + AI match"
                  color="success"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2.4 }}>
                <StatCard
                  title="GTIN Transitive"
                  value={pairDetails.by_match_type?.gtin_transitive || 0}
                  subtitle="Connected via GTIN chain"
                  color="warning"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2.4 }}>
                <StatCard
                  title="Hub Transitive"
                  value={pairDetails.by_match_type?.hub_transitive || 0}
                  subtitle="Shared hub neighbors"
                  color="info"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2.4 }}>
                <StatCard
                  title="GTIN Legacy"
                  value={pairDetails.by_match_type?.gtin_legacy || 0}
                  subtitle="Not yet migrated"
                  color="default"
                />
              </Grid>
            </Grid>

            {/* Relationship Trend Chart */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Relationship Trend</Typography>
                  <ToggleButtonGroup
                    value={trendDays}
                    exclusive
                    onChange={(e, val) => { if (val !== null) setTrendDays(val); }}
                    size="small"
                  >
                    {[7, 14, 30, 60, 90].map((d) => (
                      <ToggleButton key={d} value={d}>{d}d</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                {trendLoading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress size={28} />
                  </Box>
                ) : trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dateLabel" fontSize={12} />
                      <YAxis fontSize={12} />
                      <RechartsTooltip
                        labelFormatter={(label, payload) => {
                          if (payload?.[0]?.payload?.snapshot_date) return payload[0].payload.snapshot_date;
                          return label;
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total_count"
                        name="Total"
                        stroke="#1976d2"
                        strokeWidth={2}
                        dot={trendData.length <= 30}
                      />
                      <Line type="monotone" dataKey="embedding" name="Embedding" stroke={MATCH_TYPE_CONFIG.embedding.color} dot={false} />
                      <Line type="monotone" dataKey="gtin_direct" name="GTIN Direct" stroke={MATCH_TYPE_CONFIG.gtin_direct.color} dot={false} />
                      <Line type="monotone" dataKey="gtin_transitive" name="GTIN Transitive" stroke={MATCH_TYPE_CONFIG.gtin_transitive.color} dot={false} />
                      <Line type="monotone" dataKey="hub_transitive" name="Hub Transitive" stroke={MATCH_TYPE_CONFIG.hub_transitive.color} dot={false} />
                      <Line type="monotone" dataKey="manual_demo" name="Manual" stroke={MATCH_TYPE_CONFIG.manual_demo.color} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">
                    No trend data available yet. Snapshots are captured daily at 6:00 AM.
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Matched Products
                    {productsPagination.total > 0 && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({productsPagination.total.toLocaleString()} total
                        {searchQuery && ` matching "${searchQuery}"`})
                      </Typography>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={matchTypeFilter}
                        displayEmpty
                        onChange={(e) => setMatchTypeFilter(e.target.value)}
                        renderValue={(val) => {
                          if (!val) return 'All types';
                          const cfg = MATCH_TYPE_CONFIG[val] || {};
                          return (
                            <Chip
                              label={cfg.label || val}
                              size="small"
                              sx={{
                                backgroundColor: cfg.bgColor,
                                color: cfg.color,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          );
                        }}
                      >
                        <MenuItem value="">All types</MenuItem>
                        {Object.entries(MATCH_TYPE_CONFIG).map(([key, cfg]) => (
                          <MenuItem key={key} value={key}>
                            <Chip
                              label={cfg.label}
                              size="small"
                              sx={{
                                backgroundColor: cfg.bgColor,
                                color: cfg.color,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      placeholder="Search all products..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      sx={{ width: 280 }}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                      }}
                    />
                  </Box>
                </Box>

                {productsLoading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : products.length > 0 ? (
                  <>
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product ({retailerAName})</TableCell>
                            <TableCell>Product ({retailerBName})</TableCell>
                            <TableCell align="center">Method</TableCell>
                            <TableCell align="right">Similarity</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {products.map((match, idx) => (
                            <TableRow
                              key={idx}
                              hover
                              onClick={() => handleProductRowClick(match)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                  {match.product_a_image && (
                                    <Box
                                      component="img"
                                      src={match.product_a_image}
                                      alt=""
                                      sx={{
                                        width: 60,
                                        height: 80,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        bgcolor: 'grey.100',
                                      }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {match.product_a_name || match.product_a_raw_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      ID: {match.product_a_raw_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {match.product_a_brand || '-'} • {match.product_a_color || '-'}
                                    </Typography>
                                    {match.product_a_has_embedding === false && (
                                      <Tooltip title="This product has no embedding — GTIN match cannot be promoted to Neo4j">
                                        <Chip
                                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                                          label="No embedding"
                                          size="small"
                                          sx={{
                                            mt: 0.5,
                                            height: 18,
                                            fontSize: '0.6rem',
                                            backgroundColor: '#FFF3E0',
                                            color: '#E65100',
                                            '& .MuiChip-icon': { color: '#E65100' },
                                            '& .MuiChip-label': { px: 0.5 },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                  {match.product_b_image && (
                                    <Box
                                      component="img"
                                      src={match.product_b_image}
                                      alt=""
                                      sx={{
                                        width: 60,
                                        height: 80,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        bgcolor: 'grey.100',
                                      }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {match.product_b_name || match.product_b_raw_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      ID: {match.product_b_raw_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {match.product_b_brand || '-'} • {match.product_b_color || '-'}
                                    </Typography>
                                    {match.product_b_has_embedding === false && (
                                      <Tooltip title="This product has no embedding — GTIN match cannot be promoted to Neo4j">
                                        <Chip
                                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                                          label="No embedding"
                                          size="small"
                                          sx={{
                                            mt: 0.5,
                                            height: 18,
                                            fontSize: '0.6rem',
                                            backgroundColor: '#FFF3E0',
                                            color: '#E65100',
                                            '& .MuiChip-icon': { color: '#E65100' },
                                            '& .MuiChip-label': { px: 0.5 },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <MatchTypeChip
                                  matchType={match.match_type
                                    || (match.match_methods?.includes('gtin') ? 'gtin_direct' : 'embedding')}
                                  gtinCount={match.gtin_count}
                                  hops={match.hops}
                                  sharedHubs={match.shared_hubs}
                                  similarity={match.similarity}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {((match.similarity || 1) * 100).toFixed(0)}%
                              </TableCell>
                              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                {match.is_overridden ? (
                                  <Chip
                                    label="Disconnected"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    onDelete={() => {/* restore handled in OverridesTab */}}
                                    deleteIcon={<Tooltip title="Restore — pair reappears after next ETL run"><span>↩</span></Tooltip>}
                                  />
                                ) : (
                                  <Tooltip title="Mark as false positive and disconnect">
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0 }}
                                      onClick={() => setDisconnectModal({
                                        open: true,
                                        uuidA: match.product_a_id,
                                        uuidB: match.product_b_id,
                                        matchType: match.match_type || match.match_methods?.[0] || 'embedding',
                                      })}
                                    >
                                      Disconnect
                                    </Button>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {productsPagination.offset + 1}–{Math.min(productsPagination.offset + products.length, productsPagination.total)} of {productsPagination.total.toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          disabled={productsPagination.offset === 0 || productsLoading}
                          onClick={() => loadProducts(
                            selectedPair.retailerA,
                            selectedPair.retailerB,
                            searchQuery,
                            Math.max(0, productsPagination.offset - productsPagination.limit)
                          )}
                        >
                          Previous
                        </Button>
                        <Button
                          size="small"
                          disabled={!productsPagination.hasMore || productsLoading}
                          onClick={() => loadProducts(
                            selectedPair.retailerA,
                            selectedPair.retailerB,
                            searchQuery,
                            productsPagination.offset + productsPagination.limit
                          )}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    {searchQuery ? `No products found matching "${searchQuery}"` : 'No matched products found'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Product Pair Detail Drawer */}
        <ProductPairDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          productAId={selectedProductPair.productAId}
          productBId={selectedProductPair.productBId}
        />
        <DisconnectModal
          open={disconnectModal.open}
          productUuidA={disconnectModal.uuidA}
          productUuidB={disconnectModal.uuidB}
          matchType={disconnectModal.matchType}
          onClose={() => setDisconnectModal({ open: false, uuidA: null, uuidB: null })}
          onSuccess={() => {
            setDisconnectModal({ open: false, uuidA: null, uuidB: null });
            // Refresh the products list so the row shows the Disconnected badge
            loadProducts(selectedPair.retailerA, selectedPair.retailerB, searchQuery, productsPagination.offset);
          }}
        />
      </Box>
    );
  }

  // Matrix view
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Retailer Pairs Matrix
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadMatrixData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={relationshipType}
          exclusive
          onChange={handleRelationshipTypeChange}
          size="small"
          aria-label="relationship type"
        >
          <ToggleButton value="IDENTICAL" aria-label="identical">
            Identical (Cross-Retailer)
          </ToggleButton>
          <ToggleButton value="VARIANT_CROSS" aria-label="variant cross-retailer">
            Variants (Cross-Retailer)
          </ToggleButton>
          <ToggleButton value="VARIANT_SAME" aria-label="variant same-retailer">
            Variants (Same Retailer)
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {matrixData?.from_cache && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing cached results (updated {Math.round((Date.now() / 1000 - matrixData.timestamp))} seconds ago)
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {relationshipType === 'VARIANT_SAME'
          ? 'Click any cell to view variant matches within a retailer.'
          : 'Click any cell to view detailed match information for that retailer pair.'}
      </Typography>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Retailer</TableCell>
              {retailers.map((retailer) => (
                <TableCell
                  key={retailer.id}
                  align="center"
                  sx={{ fontWeight: 'bold', minWidth: 80 }}
                >
                  {retailer.name.split(' ')[0]}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {retailers.map((rowRetailer) => (
              <TableRow key={rowRetailer.id}>
                <TableCell sx={{ fontWeight: 'bold' }}>{rowRetailer.name}</TableCell>
                {retailers.map((colRetailer) => {
                  const pairKey = `${rowRetailer.id}|${colRetailer.id}`;
                  const pair = pairsMap[pairKey];
                  const isDiagonal = rowRetailer.id === colRetailer.id;

                  // For VARIANT_SAME: only show diagonal cells
                  // For other types: only show off-diagonal cells
                  const isDisabled = relationshipType === 'VARIANT_SAME' ? !isDiagonal : isDiagonal;
                  const displayValue = isDisabled ? '-' : pair?.total?.toLocaleString() || '0';

                  return (
                    <TableCell
                      key={colRetailer.id}
                      align="center"
                      onClick={() => !isDisabled && handleCellClick(rowRetailer.id, colRetailer.id)}
                      sx={{
                        cursor: isDisabled ? 'default' : 'pointer',
                        backgroundColor: isDisabled
                          ? 'action.disabled'
                          : getHeatColor(pair?.total, maxValue),
                        '&:hover': isDisabled
                          ? {}
                          : { backgroundColor: 'action.hover' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {displayValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Color intensity indicates match density
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 14,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              border: '1px solid #ddd',
            }}
          />
          <Typography variant="caption">Low</Typography>
          <Box
            sx={{
              width: 20,
              height: 14,
              backgroundColor: 'rgba(25, 118, 210, 0.35)',
              border: '1px solid #ddd',
            }}
          />
          <Box
            sx={{
              width: 20,
              height: 14,
              backgroundColor: 'rgba(25, 118, 210, 0.6)',
              border: '1px solid #ddd',
            }}
          />
          <Typography variant="caption">High</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ProductExplorerTab() {
  // Unified search state
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Selected product and its matches
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);

  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Debounced unified search
  useEffect(() => {
    if (searchInput.length < 2) {
      setSearchProducts([]);
      return;
    }

    const timer = setTimeout(async () => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setSearchLoading(true);
        setError(null);
        try {
          const data = await statsApi.searchProductsUnified(searchInput, { limit: 60 });
          setSearchProducts(data.products || []);
        } catch (err) {
          setError(err.message);
          setSearchProducts([]);
        } finally {
          setSearchLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  // Load matches for a selected product
  const handleSelectProduct = useCallback(async (product) => {
    setSelectedProduct(product);
    setMatchLoading(true);
    setError(null);
    setShowDetails(false);

    try {
      // Pass raw_color + raw_brand to complete the products_unique_product key
      // (retailer_id, raw_product_id, raw_color, raw_brand). Feeds where
      // raw_product_id is a style code (e.g. Berden/Divide) map one ID to many
      // colourways; without these the lookup returns multiple_results.
      const data = await statsApi.getProductMatches(product.retailer_id, {
        raw_product_id: product.raw_product_id,
        raw_color: product.raw_color,
        raw_brand: product.raw_brand,
      });
      setMatchResults(data);
    } catch (err) {
      setError(err.message);
      setMatchResults(null);
    } finally {
      setMatchLoading(false);
    }
  }, []);

  // Go back to search results
  const handleBack = () => {
    setSelectedProduct(null);
    setMatchResults(null);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Product Match Explorer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search for a product by name, brand, or ID across all retailers, then view its matches.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Unified search box */}
      {!selectedProduct && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by product name, brand, or product ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: searchLoading ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
            {searchInput.length > 0 && searchInput.length < 2 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Type at least 2 characters to search
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search results list */}
      {!selectedProduct && searchProducts.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Found {searchProducts.length} products
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Retailer</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchProducts.map((product, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ width: 60 }}>
                        {product.image_url && (
                          <Box
                            component="img"
                            src={product.image_url}
                            alt={product.raw_product_name}
                            sx={{ width: 40, height: 40, objectFit: 'contain' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.raw_product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          ID: {product.raw_product_id}
                        </Typography>
                        {product.normalized_brand && (
                          <Typography variant="caption" color="text.secondary">
                            Brand: {product.normalized_brand}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.retailer_name || product.retailer_id}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSelectProduct(product)}
                        >
                          View Matches
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* No results message */}
      {!selectedProduct && searchQuery.length >= 2 && !searchLoading && searchProducts.length === 0 && (
        <Alert severity="info">No products found matching "{searchQuery}"</Alert>
      )}

      {/* Loading matches */}
      {matchLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Selected product matches view */}
      {selectedProduct && matchResults && !matchLoading && (
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back to Search Results
          </Button>

          {/* Multiple products share this raw_product_id (e.g. Berden/Divide
              style codes that map to several colourways). Let the user pick
              the exact colourway to resolve the unique key. */}
          {matchResults.multiple_results && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {matchResults.message
                    || `This product ID maps to ${matchResults.source_products?.length || 0} colourways. Select one to view its matches.`}
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Colour</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(matchResults.source_products || []).map((product, idx) => (
                        <TableRow key={product.id || idx} hover>
                          <TableCell sx={{ width: 60 }}>
                            {product.image_url && (
                              <Box
                                component="img"
                                src={product.image_url}
                                alt={product.raw_product_name}
                                sx={{ width: 40, height: 40, objectFit: 'contain' }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {product.raw_product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              ID: {product.raw_product_id}
                            </Typography>
                            {product.normalized_brand && (
                              <Typography variant="caption" color="text.secondary">
                                Brand: {product.normalized_brand}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {product.raw_color || product.normalized_color || '-'}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleSelectProduct({ ...product, retailer_id: selectedProduct.retailer_id })}
                            >
                              View Matches
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* No product resolved for this lookup */}
          {!matchResults.multiple_results && !matchResults.source_product && (
            <Alert severity="info">
              {matchResults.error || 'No product found for this lookup.'}
            </Alert>
          )}

          {/* Single product found - show matches */}
          {matchResults.source_product && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Source Product
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {matchResults.source_product.image_url && (
                      <Box
                        component="img"
                        src={matchResults.source_product.image_url}
                        alt={matchResults.source_product.raw_product_name}
                        sx={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 1, border: '1px solid #eee' }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">
                        {matchResults.source_product.raw_product_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Product ID: {matchResults.source_product.raw_product_id}
                      </Typography>
                      {matchResults.source_product.normalized_brand && (
                        <Typography variant="body2" color="text.secondary">
                          Brand: {matchResults.source_product.normalized_brand}
                        </Typography>
                      )}

                      {/* Expandable details section */}
                      <Button
                        size="small"
                        onClick={() => setShowDetails(!showDetails)}
                        startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mt: 1, mb: 1, textTransform: 'none' }}
                      >
                        {showDetails ? 'Hide Details' : 'Show Details'}
                      </Button>

                      <Collapse in={showDetails}>
                        {/* Attribute comparison table */}
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                          <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Attribute</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Raw</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Normalized</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Brand</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_brand || '-'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.normalized_brand || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Color</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_color || '-'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.normalized_color || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Category</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_category || '-'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.normalized_category || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Pattern</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_pattern || '-'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_pattern || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Name</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_product_name || '-'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.normalized_product_name || '-'}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>ID</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.raw_product_id || '-'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{matchResults.source_product.normalized_product_id || '-'}</TableCell>
                              </TableRow>
                              {matchResults.source_product.raw_materials && (
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 500 }}>Materials</TableCell>
                                  <TableCell colSpan={2} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                    {Array.isArray(matchResults.source_product.raw_materials)
                                      ? matchResults.source_product.raw_materials.join(', ')
                                      : matchResults.source_product.raw_materials}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Extracted from image */}
                        {(matchResults.source_product.extracted_colors || matchResults.source_product.extracted_pattern) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                              Extracted from Image
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              {matchResults.source_product.extracted_colors && (
                                <Grid size={{ xs: 6 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Colors
                                  </Typography>
                                  <Typography variant="body2">
                                    {Array.isArray(matchResults.source_product.extracted_colors)
                                      ? matchResults.source_product.extracted_colors.map((c) =>
                                          typeof c === 'object' ? c.name || c.color || JSON.stringify(c) : c
                                        ).join(', ')
                                      : typeof matchResults.source_product.extracted_colors === 'object'
                                        ? JSON.stringify(matchResults.source_product.extracted_colors)
                                        : matchResults.source_product.extracted_colors}
                                  </Typography>
                                </Grid>
                              )}
                              {matchResults.source_product.extracted_pattern && (
                                <Grid size={{ xs: 6 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Pattern
                                  </Typography>
                                  <Typography variant="body2">
                                    {Array.isArray(matchResults.source_product.extracted_pattern)
                                      ? matchResults.source_product.extracted_pattern.map((p) =>
                                          typeof p === 'object' ? p.pattern || JSON.stringify(p) : p
                                        ).join(', ')
                                      : typeof matchResults.source_product.extracted_pattern === 'object'
                                        ? matchResults.source_product.extracted_pattern.pattern || JSON.stringify(matchResults.source_product.extracted_pattern)
                                        : matchResults.source_product.extracted_pattern}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}

                        {/* Descriptions */}
                        {(matchResults.source_product.description || matchResults.source_product.normalized_description) && (
                          <Box sx={{ mb: 2 }}>
                            {matchResults.source_product.description && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Raw Description
                                </Typography>
                                <Typography variant="body2" sx={{ maxWidth: { xs: '100%', sm: 600 }, fontSize: '0.85rem' }}>
                                  {matchResults.source_product.description.length > 300
                                    ? `${matchResults.source_product.description.substring(0, 300)}...`
                                    : matchResults.source_product.description}
                                </Typography>
                              </Box>
                            )}
                            {matchResults.source_product.normalized_description && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Normalized Description (Summary)
                                </Typography>
                                <Typography variant="body2" sx={{ maxWidth: { xs: '100%', sm: 600 }, fontSize: '0.85rem' }}>
                                  {(() => {
                                    const desc = matchResults.source_product.normalized_description;
                                    if (typeof desc === 'string') return desc;
                                    if (desc?.english?.summary) return desc.english.summary;
                                    if (typeof desc?.english === 'string') return desc.english;
                                    if (desc?.summary) return desc.summary;
                                    if (typeof desc?.original === 'string') return desc.original;
                                    return JSON.stringify(desc);
                                  })()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Collapse>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip
                      label={`${matchResults.total_matches || 0} matches`}
                      color={matchResults.total_matches > 0 ? 'success' : 'default'}
                      sx={{ mb: 1 }}
                    />
                    {matchResults.matches?.length > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {(() => {
                          const counts = matchResults.matches.reduce((acc, m) => {
                            const type = m.match_type || 'embedding';
                            acc[type] = (acc[type] || 0) + 1;
                            return acc;
                          }, {});
                          return Object.entries(counts)
                            .map(([type, count]) => `${MATCH_TYPE_CONFIG[type]?.label || type}: ${count}`)
                            .join(' | ');
                        })()}
                      </Typography>
                    )}
                    {matchResults.source_product.product_page_url && (
                      <Button
                        size="small"
                        href={matchResults.source_product.product_page_url}
                        target="_blank"
                        sx={{ mt: 1 }}
                      >
                        View Product
                      </Button>
                    )}
                  </Box>
                </Box>

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                  Identical Matches ({matchResults.total_matches || 0})
                </Typography>

                {matchResults.matches?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Image</TableCell>
                          <TableCell>Retailer</TableCell>
                          <TableCell>Product Name</TableCell>
                          <TableCell>Brand</TableCell>
                          <TableCell align="center">Method</TableCell>
                          <TableCell align="right">Similarity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {matchResults.matches.map((match, matchIdx) => (
                          <TableRow key={matchIdx}>
                            <TableCell sx={{ width: 60 }}>
                              {match.image_url && (
                                <Box
                                  component="img"
                                  src={match.image_url}
                                  alt={match.raw_product_name}
                                  sx={{ width: 40, height: 40, objectFit: 'contain' }}
                                />
                              )}
                            </TableCell>
                            <TableCell>{match.retailer_name || match.retailer_id}</TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 150, sm: 250 } }}>
                                {match.raw_product_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {match.raw_product_id}
                              </Typography>
                            </TableCell>
                            <TableCell>{match.normalized_brand}</TableCell>
                            <TableCell align="center">
                              <MatchTypeChip
                                matchType={match.match_type || 'embedding'}
                                gtinCount={match.gtin_count}
                                hops={match.hops}
                                sharedHubs={match.shared_hubs}
                                similarity={match.similarity}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {((match.similarity || 1) * 100).toFixed(0)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No identical matches found for this product across other retailers.</Alert>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

function FalseNegativesTab() {
  const [retailers, setRetailers] = useState([]);
  const [retailerA, setRetailerA] = useState('');
  const [retailerB, setRetailerB] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [minSimilarity, setMinSimilarity] = useState(0.80);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchStats, setSearchStats] = useState(null);
  // Pagination and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [hasSearched, setHasSearched] = useState(false);
  // Product pair drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProductPair, setSelectedProductPair] = useState({ productAId: null, productBId: null });

  const handleCandidateClick = (candidate) => {
    setSelectedProductPair({
      productAId: candidate.product_a.id,
      productBId: candidate.product_b.id,
    });
    setDrawerOpen(true);
  };

  // Load retailers on mount
  useEffect(() => {
    const loadRetailers = async () => {
      try {
        const data = await statsApi.getAllRetailerPairs();
        setRetailers(data.retailers || []);
      } catch (err) {
        console.error('Error loading retailers:', err);
      }
    };
    loadRetailers();
  }, []);

  // Load brands when both retailers are selected
  useEffect(() => {
    if (retailerA && retailerB && retailerA !== retailerB) {
      const loadBrands = async () => {
        try {
          setBrandsLoading(true);
          const data = await statsApi.getBrandsForRetailers(retailerA, retailerB);
          setBrands(data.brands || []);
          setSelectedBrand('');
        } catch (err) {
          console.error('Error loading brands:', err);
          setBrands([]);
        } finally {
          setBrandsLoading(false);
        }
      };
      loadBrands();
    } else {
      setBrands([]);
      setSelectedBrand('');
    }
  }, [retailerA, retailerB]);

  const loadCandidates = useCallback(async (search = '', offset = 0) => {
    if (!retailerA || !retailerB || !selectedBrand) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await statsApi.getFalseNegativeCandidates({
        retailer_a: retailerA,
        retailer_b: retailerB,
        normalized_brand: selectedBrand,
        min_similarity: minSimilarity,
        limit: 50,
        offset,
        search: search || undefined,
      });

      if (data.error) {
        setError(data.error);
        setCandidates([]);
      } else {
        setCandidates(data.candidates || []);
        setSearchStats({
          total: data.total_candidates,
          returned: data.returned_count,
          productsA: data.products_a_count,
          productsB: data.products_b_count,
        });
        setPagination({
          total: data.total_candidates || 0,
          limit: data.limit || 50,
          offset: data.offset || 0,
          hasMore: data.has_more || false,
        });
      }
    } catch (err) {
      setError(err.message);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [retailerA, retailerB, selectedBrand, minSimilarity]);

  const handleSearch = async () => {
    if (!retailerA || !retailerB || !selectedBrand) {
      setError('Please select both retailers and a brand');
      return;
    }
    setSearchQuery('');
    setSearchInput('');
    setHasSearched(true);
    await loadCandidates('', 0);
  };

  // Debounced search effect for product filter
  useEffect(() => {
    if (!hasSearched) return;
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        loadCandidates(searchInput, 0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, hasSearched, loadCandidates]);

  const retailerAName = retailers.find(r => r.id === retailerA)?.name || retailerA;
  const retailerBName = retailers.find(r => r.id === retailerB)?.name || retailerB;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Potential False Negatives Explorer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Find products that have high embedding similarity but no IDENTICAL relationship.
        These are potential matches the system may have missed.
      </Typography>

      {/* Search Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Retailer A</InputLabel>
                <Select
                  value={retailerA}
                  label="Retailer A"
                  onChange={(e) => setRetailerA(e.target.value)}
                >
                  {retailers.map((r) => (
                    <MenuItem key={r.id} value={r.id} disabled={r.id === retailerB}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Retailer B</InputLabel>
                <Select
                  value={retailerB}
                  label="Retailer B"
                  onChange={(e) => setRetailerB(e.target.value)}
                >
                  {retailers.map((r) => (
                    <MenuItem key={r.id} value={r.id} disabled={r.id === retailerA}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small" disabled={!brands.length || brandsLoading}>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedBrand}
                  label="Brand"
                  onChange={(e) => setSelectedBrand(e.target.value)}
                >
                  {brandsLoading ? (
                    <MenuItem value="">Loading...</MenuItem>
                  ) : (
                    brands.map((brand) => (
                      <MenuItem key={brand} value={brand}>
                        {brand}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Min Similarity: {(minSimilarity * 100).toFixed(0)}%
                </Typography>
                <Slider
                  size="small"
                  value={minSimilarity}
                  onChange={(e, val) => setMinSimilarity(val)}
                  min={0.70}
                  max={0.95}
                  step={0.05}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${(v * 100).toFixed(0)}%`}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || !retailerA || !retailerB || !selectedBrand}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {searchStats && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Found {searchStats.total} candidates.
          {` ${selectedBrand}: ${searchStats.productsA} products in ${retailerAName}, ${searchStats.productsB} in ${retailerBName}`}
        </Alert>
      )}

      {/* Results with search and pagination */}
      {hasSearched && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Candidates
                {pagination.total > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({pagination.total.toLocaleString()} total
                    {searchQuery && ` matching "${searchQuery}"`})
                  </Typography>
                )}
              </Typography>
              <TextField
                size="small"
                placeholder="Filter by product name/ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={{ width: 280 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
              />
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={32} />
              </Box>
            ) : candidates.length > 0 ? (
              <>
                <Stack spacing={2}>
                  {candidates.map((candidate, index) => (
            <Card
              key={index}
              variant="outlined"
              onClick={() => handleCandidateClick(candidate)}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                  {/* Product A */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {retailerAName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {candidate.product_a.image_url && (
                        <Box
                          component="img"
                          src={candidate.product_a.image_url}
                          alt=""
                          sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                          {candidate.product_a.raw_product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {candidate.product_a.raw_product_id}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Color: {candidate.product_a.normalized_color || '-'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Category: {candidate.product_a.normalized_category || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Similarity Score */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                    <Typography variant="h5" color="primary.main">
                      {(candidate.similarity * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      similarity
                    </Typography>
                    {Object.keys(candidate.attribute_differences).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {Object.entries(candidate.attribute_differences).map(([attr, values]) => (
                          <Chip
                            key={attr}
                            label={attr.replace('normalized_', '')}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20, mr: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Product B */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {retailerBName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {candidate.product_b.image_url && (
                        <Box
                          component="img"
                          src={candidate.product_b.image_url}
                          alt=""
                          sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                          {candidate.product_b.raw_product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {candidate.product_b.raw_product_id}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Color: {candidate.product_b.normalized_color || '-'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Category: {candidate.product_b.normalized_category || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
                  ))}
                </Stack>

                {/* Pagination controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {pagination.offset + 1}–{Math.min(pagination.offset + candidates.length, pagination.total)} of {pagination.total.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      disabled={pagination.offset === 0 || loading}
                      onClick={() => loadCandidates(searchQuery, Math.max(0, pagination.offset - pagination.limit))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      disabled={!pagination.hasMore || loading}
                      onClick={() => loadCandidates(searchQuery, pagination.offset + pagination.limit)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Alert severity="success">
                No false negative candidates found{searchQuery ? ` matching "${searchQuery}"` : ''}! All similar products appear to be properly matched.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Product Pair Detail Drawer */}
      <ProductPairDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        productAId={selectedProductPair.productAId}
        productBId={selectedProductPair.productBId}
      />
    </Box>
  );
}

export default function RelationshipsDashboard() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Relationships
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Retailer Pairs" />
          <Tab label="Product Explorer" />
          <Tab label="Potential False Negatives" />
          <Tab label="Overrides" />
        </Tabs>
      </Box>

      {activeTab === 0 && <RetailerPairsTab />}
      {activeTab === 1 && <ProductExplorerTab />}
      {activeTab === 2 && <FalseNegativesTab />}
      {activeTab === 3 && <OverridesTab />}
    </Box>
  );
}
